import React, { useEffect, useState, useCallback } from 'react';
import { listUsers, setUserClaims, setUserDisabled, AVAILABLE_CLAIMS } from '../firebase/admin';

const displayName = (u) => u.displayName || u.email || u.phoneNumber || u.uid;

const UserAdmin = ({ currentUid }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [showDisabled, setShowDisabled] = useState(false);
  const [selectedUid, setSelectedUid] = useState(null);
  const [savingClaim, setSavingClaim] = useState('');
  const [togglingDisabled, setTogglingDisabled] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const list = await listUsers();
      setUsers(list);
    } catch (err) {
      setError(err?.message || 'Failed to load users.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const selected = users.find((u) => u.uid === selectedUid) || null;

  // Disabled (rejected) accounts are hidden by default; the toggle reveals them.
  const disabledCount = users.filter((u) => u.disabled).length;
  const filtered = users.filter((u) => {
    if (!showDisabled && u.disabled) return false;
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return (
      (u.email || '').toLowerCase().includes(q) ||
      (u.displayName || '').toLowerCase().includes(q) ||
      (u.phoneNumber || '').toLowerCase().includes(q)
    );
  });

  const toggleClaim = async (user, claimKey, checked) => {
    setError('');
    setSavingClaim(claimKey);
    // Build the full desired managed-claims map for this user.
    const desired = {};
    AVAILABLE_CLAIMS.forEach((c) => {
      desired[c.key] = c.key === claimKey ? checked : !!user.claims?.[c.key];
    });
    try {
      const res = await setUserClaims(user.uid, desired);
      setUsers((prev) =>
        prev.map((u) => (u.uid === user.uid ? { ...u, claims: res.claims } : u))
      );
    } catch (err) {
      setError(err?.message || 'Failed to update claim.');
    } finally {
      setSavingClaim('');
    }
  };

  // Disable an account (blocks sign-in immediately) or re-enable it (undo a
  // rejection so they can sign in again). Claims are untouched either way —
  // grant Member to approve a re-enabled user. The backend also forbids an
  // admin from disabling their own account; the UI hides the action for self.
  const setAccountDisabled = async (user, disabled) => {
    setError('');
    setTogglingDisabled(true);
    try {
      await setUserDisabled(user.uid, disabled);
      setUsers((prev) =>
        prev.map((u) => (u.uid === user.uid ? { ...u, disabled } : u))
      );
    } catch (err) {
      setError(err?.message || `Failed to ${disabled ? 'disable' : 're-enable'} account.`);
    } finally {
      setTogglingDisabled(false);
    }
  };

  return (
    <div className="ua">
      <div className="ua-grid">
        {/* Left: user list */}
        <aside className="ua-list glass">
          <div className="ua-list-head">
            <h2>Users {!loading && <span className="ua-count">{filtered.length}</span>}</h2>
            <button className="ua-refresh" onClick={load} disabled={loading}>
              {loading ? '…' : '↻'}
            </button>
          </div>
          <input
            className="ua-search"
            type="search"
            placeholder="Search name, email, phone"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <label className="ua-filter">
            <input
              type="checkbox"
              checked={showDisabled}
              onChange={(e) => setShowDisabled(e.target.checked)}
            />
            Show disabled
            {disabledCount > 0 && <span className="ua-filter-count">{disabledCount}</span>}
          </label>
          {loading ? (
            <p className="ua-muted">Loading users…</p>
          ) : error && !users.length ? (
            <p className="ua-error">{error}</p>
          ) : (
            <ul className="ua-users">
              {filtered.map((u) => (
                <li key={u.uid}>
                  <button
                    className={`ua-user ${u.uid === selectedUid ? 'active' : ''}`}
                    onClick={() => setSelectedUid(u.uid)}
                  >
                    <span className="ua-user-name">{displayName(u)}</span>
                    <span className="ua-user-sub">{u.email || u.phoneNumber || u.uid}</span>
                    <span className="ua-tags">
                      {u.disabled ? (
                        <span className="ua-tag ua-tag-disabled">Disabled</span>
                      ) : (
                        <>
                          {u.claims?.admin && <span className="ua-tag">Admin</span>}
                          {u.claims?.member && <span className="ua-tag ua-tag-member">Member</span>}
                        </>
                      )}
                    </span>
                  </button>
                </li>
              ))}
              {!filtered.length && <li className="ua-muted">No matching users.</li>}
            </ul>
          )}
        </aside>

        {/* Right: claims editor */}
        <section className="ua-detail glass">
          {!selected ? (
            <div className="ua-empty">
              <p>Select a user to manage their claims.</p>
            </div>
          ) : (
            <>
              <div className="ua-detail-head">
                <h2>{displayName(selected)}</h2>
                <dl className="ua-meta">
                  {selected.email && (<><dt>Email</dt><dd>{selected.email}</dd></>)}
                  {selected.phoneNumber && (<><dt>Phone</dt><dd>{selected.phoneNumber}</dd></>)}
                  <dt>Providers</dt>
                  <dd>{selected.providers.join(', ') || '—'}</dd>
                  <dt>User ID</dt>
                  <dd className="ua-uid">{selected.uid}</dd>
                </dl>
              </div>

              <div className={`ua-status ${selected.disabled ? 'ua-status-disabled' : ''}`}>
                {selected.disabled ? (
                  <>
                    <span className="ua-status-text">
                      This account is <strong>disabled</strong> (rejected). Re-enabling lets
                      them sign in again; grant Member below to approve them.
                    </span>
                    <button
                      className="ua-reenable"
                      onClick={() => setAccountDisabled(selected, false)}
                      disabled={togglingDisabled}
                    >
                      {togglingDisabled ? 'Re-enabling…' : 'Re-enable account'}
                    </button>
                  </>
                ) : selected.uid === currentUid ? (
                  <span className="ua-status-text ua-status-muted">
                    This is your account.
                  </span>
                ) : (
                  <>
                    <span className="ua-status-text">
                      This account is <strong>active</strong>. Disabling it blocks sign-in
                      immediately.
                    </span>
                    <button
                      className="ua-disable"
                      onClick={() => setAccountDisabled(selected, true)}
                      disabled={togglingDisabled}
                    >
                      {togglingDisabled ? 'Disabling…' : 'Disable account'}
                    </button>
                  </>
                )}
              </div>

              <div className="ua-claims">
                <h3>Claims</h3>
                {AVAILABLE_CLAIMS.map((c) => {
                  const isSelf = selected.uid === currentUid;
                  const disableSelfAdmin = isSelf && c.key === 'admin';
                  return (
                    <label
                      key={c.key}
                      className={`ua-claim ${disableSelfAdmin ? 'disabled' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={!!selected.claims?.[c.key]}
                        disabled={savingClaim === c.key || disableSelfAdmin}
                        onChange={(e) => toggleClaim(selected, c.key, e.target.checked)}
                      />
                      <span className="ua-claim-body">
                        <span className="ua-claim-label">
                          {c.label}
                          {savingClaim === c.key && <em className="ua-saving"> saving…</em>}
                        </span>
                        <span className="ua-claim-desc">{c.description}</span>
                        {disableSelfAdmin && (
                          <span className="ua-claim-hint">
                            You can’t change your own admin access.
                          </span>
                        )}
                      </span>
                    </label>
                  );
                })}
              </div>

              {error && <p className="ua-error">{error}</p>}
            </>
          )}
        </section>
      </div>
    </div>
  );
};

export default UserAdmin;
